import firebase from 'firebase/app';
import 'firebase/auth';

// Initialize Firebase
const app = firebase.initializeApp({
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
});

export const auth = app.auth();

/**
 * Converts all Firebase auth errors into a message we want to provide to the user.
 * Can use the default message if or overwrite with a custom message.
 * @returns {{ message: string, type: string, isFirebaseError: boolean }}: Can be passed to authContext.notifications
 */
function processFirebaseError(error) {
  // for when we don't want to disclose if it's a password error, user not found
  // or any other specific error
  const genericLoginError =
    'There was an issue logging in. Please check ' +
    'your credentials and try again. If you registered using a different ' +
    'authentication method, such as signing in with Azure AD, please use that ' +
    'method to log in.';

  function errorMapping(error) {
    switch (error.code) {
      // error codes that should be hidden from the user
      case 'auth/internal-error': // new firebase obfuscated error code
      case 'auth/user-not-found':
      case 'auth/wrong-password':
        return {
          message: genericLoginError,
          type: 'error',
          isFirebaseError: true,
        };
      // error codes that should be shown to the user
      case 'auth/email-already-in-use': // only shown on sign up
      case 'auth/account-exists-with-different-credential': // only shown on sign up
      case 'auth/invalid-email':
      case 'auth/user-disabled':
      case 'auth/operation-not-allowed':
      case 'auth/weak-password':
      case 'auth/too-many-requests':
        return { message: error.message, type: 'error', isFirebaseError: true };
      // error codes that should be shown as info to the user
      case 'auth/cancelled-popup-request':
      case 'auth/popup-blocked':
      case 'auth/popup-closed-by-user':
      case 'auth/expired-action-code':
      case 'auth/invalid-action-code':
        return { message: error.message, type: 'info', isFirebaseError: true };
      default:
        // all other errors
        return {
          message:
            error.message ?? 'An unexpected error occurred. Please try again.',
          type: 'error',
          isFirebaseError: true,
        };
    }
  }

  if (!error.code || !error.message) {
    // This is not a Firebase error, so we don't know how to handle it
    throw error;
  }
  return errorMapping(error);
}

export const firebaseApplyActionCode = async (actionCode) => {
  // Apply the code to confirm the user's email address
  try {
    await auth.applyActionCode(actionCode); // returns void
  } catch (error) {
    // - 'auth/expired-action-code': Code is invalid or expired.
    // - 'auth/invalid-action-code': Code is invalid or expired.
    throw processFirebaseError(error);
  }
};

export const firebaseCheckActionCode = async (actionCode) => {
  // Confirm the code is valid and get the user's email address
  try {
    const { data } = await auth.checkActionCode(actionCode);
    // checkActionCode returns an object with two properties: data and operation
    // operation === 'EMAIL_SIGNIN' || 'PASSWORD_RESET' || 'VERIFY_EMAIL' || 'RECOVER_EMAIL'
    //                || 'REVERT_SECOND_FACTOR_ADDITION' || 'VERIFY_AND_CHANGE_EMAIL'
    // data contains the email address and other information depending on the operation
    return data.email;
  } catch (error) {
    // - 'auth/expired-action-code': Code is invalid or expired.
    // - 'auth/invalid-action-code': Code is invalid or expired.
    throw processFirebaseError(error);
  }
};

export const firebaseLogin = async (email, password) => {
  // sign in as user given email / password
  try {
    // returns user credential
    const res = await auth.signInWithEmailAndPassword(email, password);
    return {
      firebaseUser: res.user, // of type firebase.User
      additionalUserInfo: res.additionalUserInfo, // can be null
    };
  } catch (error) {
    // 'auth/user-not-found': No user found with provided email.
    // 'auth/wrong-password': Incorrect password provided for that user.
    // 'auth/user-disabled': User account is disabled.
    // 'auth/account-exists-with-different-credential': Email already exists with a different auth provider.
    throw processFirebaseError(error);
  }
};

export const firebaseLoginWithAzurePopup = async () => {
  // Sign in a user using the Microsoft Azure AD provider with a popup.
  const provider = new firebase.auth.OAuthProvider('microsoft.com');
  try {
    const res = await auth.signInWithPopup(provider);
    return {
      firebaseUser: res.user, // of type firebase.User
      additionalUserInfo: res.additionalUserInfo, // can be null
    };
  } catch (error) {
    // - 'auth/cancelled-popup-request': Multiple pop-ups were triggered.
    // - 'auth/popup-blocked': Browser blocked the popup.
    // - 'auth/popup-closed-by-user': Popup was closed by the user before completing the sign in.
    // - 'auth/account-exists-with-different-credential': Email already exists with a different auth provider.
    throw processFirebaseError(error);
  }
};

export const firebaseLogout = async () => {
  try {
    await auth.signOut(); // returns void
  } catch (error) {
    throw processFirebaseError(error);
  }
};

export const firebaseSignUp = async (email, password) => {
  // Create a new firebaseEntity account with the given email and password
  try {
    const res = await auth.createUserWithEmailAndPassword(email, password);
    return {
      firebaseUser: res.user, // of type firebase.User
      additionalUserInfo: res.additionalUserInfo, // can be null
    };
  } catch (error) {
    // - 'auth/email-already-in-use': The email address is already in use by another account.
    // - 'auth/invalid-email': The email address is not valid.
    // - 'auth/operation-not-allowed': Email/password accounts are not enabled.
    // - 'auth/weak-password': Need stronger password.
    throw processFirebaseError(error);
  }
};

export const firebaseSendEmailVerification = async (firebaseUser) => {
  try {
    return await firebaseUser.sendEmailVerification(); // returns void
  } catch (error) {
    // - 'auth/invalid-email': The email address is not a valid email.
    // - 'auth/user-not-found': No user found with that email.
    throw processFirebaseError(error);
  }
};

export const firebaseSendPasswordResetEmail = async (email) => {
  try {
    await auth.sendPasswordResetEmail(email); // returns void
  } catch (error) {
    // - 'auth/invalid-email': The email address is not a valid email.
    // - 'auth/user-not-found': No user found with that email.
    throw processFirebaseError(error);
  }
};

export const firebaseResetPassword = async (oobCode, newPassword) => {
  // Completes the password reset process, given a confirmation code and new password.
  try {
    const userEmail = await auth.verifyPasswordResetCode(oobCode);
    await auth.confirmPasswordReset(oobCode, newPassword); // returns void
    return userEmail;
  } catch (error) {
    // - 'auth/expired-action-code': Code is invalid or expired.
    // - 'auth/invalid-action-code': Code is invalid or expired.
    // - 'auth/weak-password': Password provided is too weak.
    throw processFirebaseError(error);
  }
};
